export type RecurringFreq = "daily" | "weekly" | "fortnightly" | "monthly" | "annually" | "one_time";

export const RECURRING_COST_DEFAULT_CURRENCY = "USD" as const;

export const RECURRING_FREQUENCY_CONFIG: Record<RecurringFreq, { label: string; perYear: number }> = {
  daily: { label: "Daily", perYear: 365 },
  weekly: { label: "Weekly", perYear: 52 },
  fortnightly: { label: "Fortnightly", perYear: 26 },
  monthly: { label: "Monthly", perYear: 12 },
  annually: { label: "Annually", perYear: 1 },
  one_time: { label: "One-off", perYear: 0 },
};

export function getRecurringOccurrencesPerYear(frequency: RecurringFreq): number {
  return RECURRING_FREQUENCY_CONFIG[frequency].perYear;
}

export function getRecurringDefaults(titleRaw: string): {
  cost: number;
  freq: RecurringFreq;
  unitLabel: string;
  showOneTime: boolean;
  habitNote: boolean;
} {
  const t = titleRaw.toLowerCase();
  if (t.includes("smoking")) return { cost: 12.50, freq: "daily", unitLabel: "pack of cigarettes", showOneTime: false, habitNote: true };
  if (t.includes("vaping")) return { cost: 6.00, freq: "weekly", unitLabel: "pod / coil", showOneTime: false, habitNote: true };
  if (t.includes("hangover")) return { cost: 40.00, freq: "weekly", unitLabel: "night out", showOneTime: false, habitNote: true };
  if (t.includes("coffee")) return { cost: 3.50, freq: "daily", unitLabel: "coffee", showOneTime: false, habitNote: true };
  if (t.includes("school lunch")) return { cost: 3.20, freq: "daily", unitLabel: "school lunch", showOneTime: false, habitNote: false };
  if (t.includes("lunch")) return { cost: 8.50, freq: "daily", unitLabel: "lunch", showOneTime: false, habitNote: false };
  if (t.includes("takeaway")) return { cost: 22.00, freq: "weekly", unitLabel: "takeaway order", showOneTime: false, habitNote: false };
  if (t.includes("recipe")) return { cost: 12.00, freq: "weekly", unitLabel: "recipe batch", showOneTime: false, habitNote: false };
  if (t.includes("bbq") || t.includes("garden bbq")) return { cost: 80.00, freq: "monthly", unitLabel: "BBQ session", showOneTime: false, habitNote: false };
  if (t.includes("eating out")) return { cost: 35.00, freq: "weekly", unitLabel: "meal out", showOneTime: false, habitNote: false };
  if (t.includes("meal")) return { cost: 15.00, freq: "weekly", unitLabel: "meal", showOneTime: false, habitNote: false };
  if (t.includes("pet food")) return { cost: 45.00, freq: "monthly", unitLabel: "month of pet food", showOneTime: true, habitNote: false };
  if (t.includes("dog walker")) return { cost: 15.00, freq: "weekly", unitLabel: "walk", showOneTime: false, habitNote: false };
  if (t.includes("boarding")) return { cost: 30.00, freq: "weekly", unitLabel: "night boarding", showOneTime: true, habitNote: false };
  if (t.includes("laundry")) return { cost: 3.50, freq: "weekly", unitLabel: "wash load", showOneTime: false, habitNote: false };
  if (t.includes("dry clean")) return { cost: 15.00, freq: "monthly", unitLabel: "dry cleaning trip", showOneTime: false, habitNote: false };
  if (t.includes("ironing")) return { cost: 25.00, freq: "monthly", unitLabel: "ironing session", showOneTime: false, habitNote: false };
  if (t.includes("netflix")) return { cost: 17.99, freq: "monthly", unitLabel: "Netflix subscription", showOneTime: false, habitNote: false };
  if (t.includes("streaming")) return { cost: 15.99, freq: "monthly", unitLabel: "streaming subscription", showOneTime: false, habitNote: false };
  if (t.includes("magazine")) return { cost: 6.99, freq: "monthly", unitLabel: "magazine", showOneTime: false, habitNote: false };
  if (t.includes("newspaper")) return { cost: 2.20, freq: "daily", unitLabel: "newspaper", showOneTime: false, habitNote: false };
  if (t.includes("book")) return { cost: 9.99, freq: "monthly", unitLabel: "book", showOneTime: false, habitNote: false };
  if (t.includes("mobile data")) return { cost: 25.00, freq: "monthly", unitLabel: "data plan", showOneTime: false, habitNote: false };
  if (t.includes("roaming")) return { cost: 8.00, freq: "daily", unitLabel: "roaming day", showOneTime: false, habitNote: false };
  if (t.includes("international call")) return { cost: 0.25, freq: "weekly", unitLabel: "international call minute", showOneTime: false, habitNote: false };
  if (t.includes("voip")) return { cost: 8.00, freq: "monthly", unitLabel: "VOIP plan", showOneTime: false, habitNote: false };
  if (t.includes("text vs app")) return { cost: 5.00, freq: "monthly", unitLabel: "SMS plan", showOneTime: false, habitNote: false };
  if (t.includes("food waste")) return { cost: 30.00, freq: "monthly", unitLabel: "wasted food", showOneTime: false, habitNote: false };
  if (t.includes("parking")) return { cost: 5.00, freq: "daily", unitLabel: "parking session", showOneTime: false, habitNote: false };
  if (t.includes("toll")) return { cost: 3.00, freq: "daily", unitLabel: "toll crossing", showOneTime: false, habitNote: false };
  return { cost: 20.00, freq: "monthly", unitLabel: "item", showOneTime: false, habitNote: false };
}

export function calculateRecurringCost(input: { cost: number; quantity: number; frequency: RecurringFreq }) {
  const costPerOccurrence = input.cost * input.quantity;
  if (input.frequency === "one_time") {
    return { isOneTime: true, totalCost: costPerOccurrence, perDay: 0, perWeek: 0, perMonth: 0, perYear: 0, per5year: 0 };
  }
  const perYear = costPerOccurrence * getRecurringOccurrencesPerYear(input.frequency);
  return {
    isOneTime: false,
    totalCost: costPerOccurrence,
    perDay: +((perYear / 365).toFixed(2)),
    perWeek: +((perYear / 52).toFixed(2)),
    perMonth: +((perYear / 12).toFixed(2)),
    perYear: +perYear.toFixed(2),
    per5year: Math.round(perYear * 5),
  };
}

export type NetflixCinemaDefaults = {
  cinemaTicketPerPerson: number;
  peoplePerTrip: number;
  snacksPerPerson: number;
  parkingPerTrip: number;
  cinemaTripsPerMonth: number;
  netflixMonthly: number;
};

export function isNetflixCinemaTitle(titleRaw: string): boolean {
  const title = titleRaw.toLowerCase();
  return title.includes("netflix") && title.includes("cinema");
}

export function getNetflixCinemaDefaults(_titleRaw: string): NetflixCinemaDefaults {
  return {
    cinemaTicketPerPerson: 12,
    peoplePerTrip: 2,
    snacksPerPerson: 6,
    parkingPerTrip: 4,
    cinemaTripsPerMonth: 2,
    netflixMonthly: 17.99,
  };
}

export function calculateNetflixCinemaComparison(input: NetflixCinemaDefaults) {
  const cinemaOutingCost = (input.cinemaTicketPerPerson + input.snacksPerPerson) * input.peoplePerTrip + input.parkingPerTrip;
  const cinemaMonthlyCost = cinemaOutingCost * input.cinemaTripsPerMonth;
  const monthlyDifference = Math.abs(cinemaMonthlyCost - input.netflixMonthly);
  const breakEvenTrips = cinemaOutingCost > 0 ? input.netflixMonthly / cinemaOutingCost : null;
  return {
    cinemaOutingCost,
    cinemaMonthlyCost,
    netflixMonthly: input.netflixMonthly,
    monthlyDifference,
    cheaperOption: cinemaMonthlyCost <= input.netflixMonthly ? "Cinema" : "Netflix",
    breakEvenTrips,
  };
}
